import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Chip,
  Autocomplete,
  FormHelperText,
  Alert,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getTemplateCreationUser, getUserById } from "../../../store/api/users";
import OrganisatioFilter from "../Statisctics/OrganisationFilter";
import { roles } from "../../../constants/routes";
import Button from "../../common/Button";

interface UserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (user: User) => void;
  isEdit: boolean;
  IdUserToEdit?: string;
  role?: string;
  isSubmitting?: boolean; // Add this prop to track mutation loading state
  submitError?: string; // Add this prop to track mutation error
}

interface User {
  id?: string;
  fullName: string;
  email: string;
  locations: string;
  dateCreated: string;
  userTypes: string;
  organisationId?: string;
  groupIds: string[];
}

interface FormErrors {
  fullName: string;
  email: string;
  userTypes: string;
  locations: string;
}

interface Location {
  id: string;
  title: string;
  isSelected: boolean;
}

interface UserType {
  id: string;
  title: string;
  isSelected: boolean;
}

interface UserGroup {
  id: string;
  title: string;
  isSelected?: boolean;
}

interface organisationType {
  id: string;
  title: string;
  isSelected: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  open,
  onClose,
  onSubmit,
  isEdit,
  IdUserToEdit,
  role,
  isSubmitting = false, // Default to false
  submitError, // Error from mutation
}) => {
  const [user, setUser] = useState<User>({
    id: "",
    fullName: "",
    email: "",
    locations: "",
    dateCreated: new Date().toISOString().split("T")[0],
    userTypes: "",
    organisationId: "",
    groupIds: [],
  });

  // Add state for form validation errors
  const [errors, setErrors] = useState<FormErrors>({
    fullName: "",
    email: "",
    userTypes: "",
    locations: "",
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [locations, setLocations] = useState<Location[]>([]);
  const [userTypeList, setUserTypeList] = useState<UserType[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [organisation, setOrganisation] = useState<organisationType>();

  const { data: creationData, isLoading: isCreationLoading } = useQuery({
    queryKey: ["UserLocationAndType"],
    queryFn: () => getTemplateCreationUser(),
    enabled: open && !isEdit,
  });

  const { data: userData, isLoading: isUserDataLoading } = useQuery({
    queryKey: ["userToEdit", IdUserToEdit],
    queryFn: () => getUserById({ id: IdUserToEdit }),
    enabled: open && isEdit && !!IdUserToEdit,
  });

  useEffect(() => {
    if (!isCreationLoading && creationData && !isEdit) {
      setLocations(creationData?.locations ?? []);
      setUserTypeList(creationData?.userTypes ?? []);
      setUserGroups(creationData?.groups ?? []);

      // Reset form for new user
      setUser({
        id: "",
        fullName: "",
        email: "",
        locations: "",
        dateCreated: new Date().toISOString().split("T")[0],
        userTypes: "",
        organisationId: "",
        groupIds: [],
      });

      setErrors({
        fullName: "",
        email: "",
        userTypes: "",
        locations: "",
      });
      setFormSubmitted(false);
    }
  }, [creationData, isCreationLoading, isEdit, open]);

  useEffect(() => {
    if (!isUserDataLoading && userData && isEdit) {
      setLocations(userData?.locations ?? []);
      setUserTypeList(userData?.userTypes ?? []);
      setUserGroups(userData?.groups ?? []);

      setUser({
        fullName: userData.fullName || "",
        email: userData.email || "",
        locations: userData.locations.find((el) => el.isSelected)?.id || "",
        dateCreated:
          userData.dateCreated || new Date().toISOString().split("T")[0],
        userTypes: userData.userTypes.find((el) => el.isSelected)?.id || "",
        groupIds:
          userData.groups
            ?.filter((group) => group.isSelected)
            .map((group) => group.id) || [],
      });

      setErrors({
        fullName: "",
        email: "",
        userTypes: "",
        locations: "",
      });
      setFormSubmitted(false);
    }
  }, [userData, isUserDataLoading, isEdit, open]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      fullName: "",
      email: "",
      userTypes: "",
      locations: "",
    };

    let isValid = true;

    if (!user.fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    }

    if (!user.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!isValidEmail(user.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!user.userTypes) {
      newErrors.userTypes = "User type is required";
      isValid = false;
    }

    if (!user.locations) {
      newErrors.locations = "Location is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });

    if (formSubmitted && value.trim() && errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const getSelectedUserGroups = () => {
    return userGroups.filter((group) => user.groupIds.includes(group.id));
  };

  const handleGroupAutocompleteChange = (
    _event: React.SyntheticEvent,
    newValue: UserGroup[]
  ) => {
    setUser({
      ...user,
      groupIds: newValue.map((group) => group.id),
    });
  };

  // Add handlers for select all and unselect all
  const handleSelectAllGroups = () => {
    setUser({
      ...user,
      groupIds: userGroups.map((group) => group.id),
    });
  };

  const handleUnselectAllGroups = () => {
    setUser({
      ...user,
      groupIds: [],
    });
  };

  const handleSubmit = () => {
    setFormSubmitted(true);

    if (validateForm()) {
      onSubmit({ ...user, organisationId: organisation?.id });
    }
  };

  const isLoading = isEdit ? isUserDataLoading : isCreationLoading;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "90%", md: "500px" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          p: 4,
          m: "auto",
          mt: "2%",
          borderRadius: 2,
          boxShadow: 24,
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" mb={2}>
          {isEdit ? "Edit User" : "Add a new single user"}
        </Typography>

        {isLoading ? (
          <Typography>Loading user data...</Typography>
        ) : (
          <>
            <TextField
              fullWidth
              required
              label="Full Name"
              name="fullName"
              value={user.fullName}
              onChange={handleInputChange}
              margin="normal"
              error={!!errors.fullName}
              helperText={errors.fullName}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={user.email}
              type="email"
              required
              onChange={handleInputChange}
              margin="normal"
              disabled={isEdit}
              error={!!errors.email}
              helperText={errors.email}
            />

            {/* Location Select */}
            <FormControl fullWidth margin="normal" error={!!errors.locations}>
              <InputLabel required>Location</InputLabel>
              <Select
                name="locations"
                value={user.locations}
                onChange={handleInputChange}
                label="Location *"
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.locations && (
                <FormHelperText>{errors.locations}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Date Added"
              name="dateCreated"
              value={user.dateCreated}
              onChange={handleInputChange}
              margin="normal"
              disabled
            />

            <FormControl fullWidth margin="normal" error={!!errors.userTypes}>
              <InputLabel required>User Type</InputLabel>
              <Select
                name="userTypes"
                value={user.userTypes}
                onChange={handleInputChange}
                label="User Type *"
              >
                {userTypeList.map((userType) => (
                  <MenuItem key={userType.id} value={userType.id}>
                    {userType.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.userTypes && (
                <FormHelperText>{errors.userTypes}</FormHelperText>
              )}
            </FormControl>

            {/* User Groups Autocomplete with Select All/Unselect All in dropdown */}
            <Autocomplete
              multiple
              id="user-groups-autocomplete"
              // options={userGroups}
              getOptionLabel={(option) => option.title}
              value={getSelectedUserGroups()}
              onChange={handleGroupAutocompleteChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="User Groups"
                  margin="normal"
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.title}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              ListboxProps={{
                style: { maxHeight: "200px" },
              }}
              renderOption={(props, option, { selected }) => {
                // Check if this is one of our custom action buttons
                if (
                  option.id === "select-all" ||
                  option.id === "unselect-all"
                ) {
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        padding: "8px 16px",
                        margin: "4px 8px",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "action.disabled",
                          color: "action.disabled",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (option.id === "select-all") {
                          handleSelectAllGroups();
                        } else {
                          handleUnselectAllGroups();
                        }
                      }}
                    >
                      {option.title}
                    </Box>
                  );
                }

                // Regular option rendering
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      "& .MuiAutocomplete-option": {
                        padding: "8px 16px",
                      },
                    }}
                  >
                    {option.title}
                  </Box>
                );
              }}
              options={[
                { id: "select-all", title: "SELECT ALL", isSelected: false },
                {
                  id: "unselect-all",
                  title: "UNSELECT ALL",
                  isSelected: false,
                },
                ...userGroups,
              ]}
            />

            {roles.SuperAdmin === role && (
              <OrganisatioFilter setOrganisation={setOrganisation} />
            )}
          </>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            onClick={handleSubmit}
            text={isEdit ? "Edit User" : "Add User"}
            disabled={isLoading || isSubmitting}
            isLoading={isSubmitting} // Pass loading state to button
          />

          {/* Loading message */}
          {isSubmitting && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 1 }}
            >
              {isEdit
                ? "Updating your user, this may take a few seconds."
                : "Adding your new user, this may take a few seconds."}
            </Typography>
          )}

          {/* Error message */}
          {submitError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError}
            </Alert>
          )}

          <Button onClick={onClose} text="Close" backgroundColor="white" />
        </Box>
      </Box>
    </Modal>
  );
};

export default UserModal;
